import AddCircleIcon from '@mui/icons-material/AddCircle';
import Info from '@mui/icons-material/Info';
import { Box, FormGroupLabel, Modal, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';

import EditableItem from '~/components/EditableItem';
import FormSpacer from '~/components-ds/FormSpacer';
import LinkButton from '~/components-ds/LinkButton';
import { MAX_PLUMBING_LICENCES } from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useCoverDetails } from '~/hooks';
import { updatePlumbingLicences } from '~/slices/coverDetails';
import { PlumbingLicence } from '~/types/cover';

import { UpsertPlumber } from './UpsertPlumber';

const modalType = ['ADD_ITEM', 'EDIT_ITEM', 'DELETE_ITEM', 'MAX_LIMIT'] as const;

export const PlumbingLicences = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBusinessLiabilityPlumbing',
  });

  const { plumbingLicences } = useCoverDetails();

  const dispatch = useAppDispatch();
  const [editItem, setEditItem] = useState<number>();
  const [showModal, setShowModal] = useState<(typeof modalType)[number] | null>(null);

  const onCloseModal = () => {
    setShowModal(null);
    setEditItem(undefined);
  };

  const onInsertPlumber = (entity: PlumbingLicence) => {
    dispatch(updatePlumbingLicences([...(plumbingLicences ?? []), entity]));
    onCloseModal();
  };

  const onDeleteItem = (index: number) => {
    setEditItem(index);
    setShowModal('DELETE_ITEM');
  };

  const onEditItem = (index: number) => {
    setEditItem(index);
    setShowModal('EDIT_ITEM');
  };

  const onDeletePlumber = async () => {
    const plumber = editItem !== undefined ? plumbingLicences?.[editItem] : null;
    if (!plumber) return;

    await dispatch(updatePlumbingLicences((plumbingLicences ?? []).filter((l) => l !== plumber)));
    onCloseModal();
  };

  const onUpdatePlumber = (entity: PlumbingLicence, index: number) => {
    dispatch(
      updatePlumbingLicences((plumbingLicences ?? []).map((p, i) => (i === index ? entity : p)))
    );
    setEditItem(undefined);
  };

  const onAddPlumber = async () => {
    if ((plumbingLicences?.length ?? 0) < MAX_PLUMBING_LICENCES) {
      setShowModal('ADD_ITEM');
      return;
    }

    setShowModal('MAX_LIMIT');
  };

  return (
    <>
      <Box textAlign="center">
        <FormGroupLabel title={getTextContent('title')} />
        <Typography>{getTextContent('description')}</Typography>
        <FormSpacer variant="input" />
        <EditableItem list={plumbingLicences ?? []} onEdit={onEditItem} onDelete={onDeleteItem} />
        {(!plumbingLicences || plumbingLicences.length < MAX_PLUMBING_LICENCES) && (
          <Box display="flex" justifyContent="center">
            <LinkButton
              onClick={onAddPlumber}
              dataTrack="Add another plumbing licence"
              label={
                (plumbingLicences?.length ?? 0) > 0
                  ? getTextContent('buttons.addAnotherLicence')
                  : getTextContent('buttons.addLicence')
              }
              muiIcon={AddCircleIcon}
            />
          </Box>
        )}
      </Box>
      {showModal === 'ADD_ITEM' && (
        <UpsertPlumber onAdd={onInsertPlumber} mode="insert" onClose={onCloseModal} />
      )}
      {showModal === 'EDIT_ITEM' && editItem !== undefined && (
        <UpsertPlumber
          onUpdate={onUpdatePlumber}
          onClose={onCloseModal}
          mode="update"
          entity={plumbingLicences?.[editItem]}
          index={editItem}
        />
      )}

      {showModal === 'MAX_LIMIT' && (
        <Modal
          open
          id="max-limit-modal"
          title={getTextContent('fields.maxLimitModal.title')}
          primaryBtnProps={{
            label: getSharedContent('gotItButtonLabel'),
            onClick: onCloseModal,
          }}
          iconProps={{
            muiIcon: Info,
          }}
        >
          <Typography>{getJSXContent('content.maxLimitModal.description')}</Typography>
        </Modal>
      )}

      {editItem !== undefined && showModal === 'DELETE_ITEM' && (
        <Modal
          open
          id="delete-modal"
          title={getTextContent('fields.removeModal.title')}
          primaryBtnProps={{
            label: getSharedContent('removeButtonLabel'),
            onClick: onDeletePlumber,
          }}
          secondaryBtnProps={{
            label: getSharedContent('cancelModalButtonLabel'),
            onClick: onCloseModal,
          }}
          iconProps={{
            muiIcon: Info,
          }}
        >
          <Typography>{getTextContent('fields.removeModal.description')}</Typography>
        </Modal>
      )}
    </>
  );
};
